import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { createStructuredSelector } from 'reselect';
import { connect } from 'react-redux';
import { DestinationDesktop, HotelDateDesktop } from './DesktopFields';
import { SegmentContext } from '../../FlightsManager/Segment/SegmentManager/Context';
import { FieldType, SearchSegment, DateType } from '../../../models';
import { getSegments, showKidsErrors } from '../../../redux/currentSearch/selectors';
import { useFocusField } from '../useFocusField';
import { HotelStandaloneRoomSelectorDesktop } from '../../RoomSelector/HotelStandalonePaxSelector';
import { HSA_SEARCH_FORM, withTrackComponentDisplayed } from '../../../utils/HtmlEvents';

import {
    ClickTrackArrival,
    ClickTrackDates,
    ClickTrackRooms,
} from '../SearchFormFieldsClickableWrapper';
import { Div } from '../../../layouts';

const getDefaultSegment = (addValue = 0) => ({ id: Date.now() + addValue });

const HotelStandaloneSearchFormDesktop = ({ segments, showKidsErrors }) => {
    const [fieldFocused, setFieldFocused] = useFocusField(segments, showKidsErrors);
    const onChangeFocus = (field, focus, index = 0) => {
        focus && setFieldFocused({ leg: index, field: field });
    };
    const onChangeFocusReturnLeg = (field, focus) => {
        onChangeFocus(field, focus, 1);
    };
    const [firstSegment, secondSegment] = segments;
    const firstSegmentValue = useMemo(() => (firstSegment || getDefaultSegment(1)), [firstSegment]);
    const secondSegmentValue = useMemo(() => (secondSegment || getDefaultSegment(2)), [secondSegment]);
    if (!secondSegment && segments.length === 1) {
        segments.push(secondSegmentValue);
    }

    const fieldOpen = fieldFocused ? fieldFocused.field : null;
    const legFieldOpen = fieldFocused ? fieldFocused.leg : null;

    return (
        <>
            <div className="odf-flex-item">
                <div className="odf-flex-container-row odf-flex-container-nogutter odf-flex-container-align-stretch">
                    <SegmentContext.Provider value={{ segment: firstSegmentValue }}>
                        <Div className="odf-flex-item odf-flex-item-span-6">
                            <ClickTrackArrival>
                                <DestinationDesktop
                                    fieldFocused={fieldOpen}
                                    onChangeFocus={onChangeFocus}
                                />
                            </ClickTrackArrival>
                        </Div>
                        <div
                            className="odf-flex-item odf-flex-item-span-3"
                            data-testid="departure-date-picker"
                        >
                            <ClickTrackDates>
                                <HotelDateDesktop
                                    open={fieldOpen === FieldType.DATE && legFieldOpen === 0}
                                    dateType={DateType.FROM}
                                    onChangeFocus={onChangeFocus}
                                    placeholderKey="prime.hotel.checkin"
                                />
                            </ClickTrackDates>
                        </div>
                    </SegmentContext.Provider>
                    <SegmentContext.Provider value={{ segment: secondSegmentValue }}>
                        <div
                            className="odf-flex-item odf-flex-item-span-3"
                            data-testid="return-date-picker"
                        >
                            <ClickTrackDates>
                                <HotelDateDesktop
                                    open={fieldOpen === FieldType.DATE && legFieldOpen === 1}
                                    dateType={DateType.UNTIL}
                                    onChangeFocus={onChangeFocusReturnLeg}
                                    placeholderKey="prime.hotel.checkout"
                                />
                            </ClickTrackDates>
                        </div>
                    </SegmentContext.Provider>
                </div>
            </div>
            <div className="odf-flex-item odf-flex-item-sm SegmentManager-Rooms-Pax">
                <ClickTrackRooms>
                    <HotelStandaloneRoomSelectorDesktop
                        fieldFocused={fieldOpen}
                        setFieldFocused={onChangeFocus}
                    />
                </ClickTrackRooms>
            </div>
        </>
    );
};

HotelStandaloneSearchFormDesktop.propTypes = {
    segments: PropTypes.arrayOf(SearchSegment),
    showKidsErrors: PropTypes.bool,
};
HotelStandaloneSearchFormDesktop.defaultProps = {
    segments: [],
    showKidsErrors: false,
};

const mapStateToPropsSelector = createStructuredSelector({
    segments: getSegments,
    showKidsErrors: showKidsErrors,
});

export const ConnectedHotelStandaloneSearchFormDesktop = connect(
    mapStateToPropsSelector,
    {},
)(withTrackComponentDisplayed(HotelStandaloneSearchFormDesktop, HSA_SEARCH_FORM));
